import { MINOR_UNITS } from "@platform/contracts";

/** Money on the wire (contracts section 2): integer minor units as a string, and the ISO 4217 code. */
export interface WireMoney {
  readonly amountMinor: string;
  readonly currency: string;
}

type Locale = "en" | "zh";

const intlLocale = (locale: Locale) => (locale === "zh" ? "zh-CN" : "en-SG");

/**
 * The currency's minor digits as the platform counts them (ISO 4217): 2 for SGD, 0 for JPY. Intl is not asked, since
 * it disagrees for some currencies (it gives IDR none), and an amount read with the wrong digits is off a hundredfold.
 */
function minorDigits(currency: string): number {
  return (MINOR_UNITS as Readonly<Record<string, number>>)[currency] ?? 2;
}

/** Integer minor units as an exact decimal string, never through a float: "1250000" SGD is "12500.00". */
export function decimalAmount(money: WireMoney): string {
  const digits = minorDigits(money.currency);
  const negative = money.amountMinor.startsWith("-");
  const units = (negative ? money.amountMinor.slice(1) : money.amountMinor).padStart(digits + 1, "0");
  const whole = units.slice(0, units.length - digits);
  const fraction = units.slice(units.length - digits);
  return `${negative ? "-" : ""}${whole}${digits === 0 ? "" : `.${fraction}`}`;
}

/**
 * A decimal a person typed ("12.5") as integer minor units ("1250"), by string alone; undefined when it is not a
 * plain non-negative number or has more decimals than the currency.
 */
export function minorAmount(text: string, currency: string): string | undefined {
  const digits = minorDigits(currency);
  const match = /^(\d{1,15})(?:\.(\d+))?$/.exec(text.trim());
  if (match === null) return undefined;
  const fraction = match[2] ?? "";
  if (fraction.length > digits) return undefined;
  return `${match[1]}${fraction.padEnd(digits, "0")}`.replace(/^0+(?=\d)/, "");
}

/**
 * Money in the reader's locale with its ISO code ("SGD 1,100.00"), since a bare "$" is ambiguous to buyers abroad;
 * formatted from the exact decimal string (Intl formats strings without a float).
 */
export function formatMoney(money: WireMoney, locale: Locale): string {
  const digits = minorDigits(money.currency);
  const format = new Intl.NumberFormat(intlLocale(locale), {
    style: "currency",
    currency: money.currency,
    currencyDisplay: "code",
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  });
  return format.format(decimalAmount(money) as `${number}`);
}

/** A quantity string ("0.5", "1200") in the reader's locale. */
export function formatQuantity(quantity: string, locale: Locale): string {
  return new Intl.NumberFormat(intlLocale(locale), { maximumFractionDigits: 3 }).format(
    quantity as `${number}`,
  );
}
